import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Card from '../components/Card';
import MemberCard from '../components/MemberCard';
import Footer from '../components/Footer';
import { icons } from 'lucide-react';
import { db } from '../firebaseConfig';
import heroBg from '../img/home-bg.png';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';

const App = () => {
    const [vehicles, setVehicles] = useState([]);
    const [members, setMembers] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [homepageContent, setHomepageContent] = useState(null);
    const [siteContent, setSiteContent] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setPageLoading(true);
            
            const vehiclesSnapshot = await getDocs(collection(db, 'vehicles'));
            setVehicles(vehiclesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            
            const membersSnapshot = await getDocs(collection(db, 'members'));
            setMembers(membersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const homepageDocRef = doc(db, 'content', 'homepage');
            const siteContentDocRef = doc(db, 'content', 'site');

            let homepageLoaded = false;
            let siteContentLoaded = false;

            const checkLoading = () => {
                if (homepageLoaded && siteContentLoaded) {
                    setPageLoading(false);
                }
            };

            const unsubHomepage = onSnapshot(homepageDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    setHomepageContent(docSnap.data());
                } else {
                    console.log("No such document for homepage content!");
                    setHomepageContent({});
                }
                homepageLoaded = true;
                checkLoading();
            });

            const unsubSiteContent = onSnapshot(siteContentDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    setSiteContent(docSnap.data());
                } else {
                    console.log("No such document for site content!");
                    setSiteContent({});
                }
                siteContentLoaded = true;
                checkLoading();
            });

            return () => {
                unsubHomepage();
                unsubSiteContent();
            };
        };
        fetchData();
    }, []);

    const getSectionStyle = (sectionKey) => {
        const sectionConfig = siteContent?.sectionBackgrounds?.[sectionKey];
        if (!sectionConfig) return {};

        if (sectionConfig.type === 'gradient') {
            return { background: `linear-gradient(${sectionConfig.angle}deg, ${sectionConfig.color1}, ${sectionConfig.color2})` };
        }
        return { backgroundColor: sectionConfig.color1 };
    };

    return (
        <>
            <Navbar />
            {pageLoading ? (
                <div className="flex justify-center items-center h-screen bg-gray-900 text-white text-2xl">
                    <p>Načítám stránku...</p>
                </div>
            ) : (
                <>
                    <header
                        className="hero-section text-white py-20 md:py-32 relative"
                        style={
                            siteContent?.heroImage?.url
                                ? { backgroundImage: `url(${siteContent.heroImage.url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                : { backgroundImage: `url(${heroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        }
                    >
                        {homepageContent?.hero ? (
                            <Hero content={homepageContent.hero} siteContent={siteContent} />
                        ) : (
                            <p>Loading Hero section...</p>
                        )}
                        <section className="container mx-auto px-4 py-12 mt-20 relative z-20">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {vehicles.length === 0 ? (
                                    <p>No vehicles available.</p>
                                ) : (
                                    vehicles.slice(0, 3).map(vehicle => (
                                        <Card key={vehicle.id} image={vehicle.photoURL} title={vehicle.name} description={vehicle.description} />
                                    )))}
                            </div>
                        </section>
                    </header>
                    <div className="h-2 bg-white w-full"></div>
                    <section id='about' className="about-us-section relative py-12 md:py-16 lg:py-20" style={getSectionStyle('about')}>
                        <div className="section-title-bg mt-5 section-title-right bg-white text-gray-900 py-3 px-12 absolute top-0 right-0 font-bold text-2xl rounded-bl-2xl transform -translate-y-1/2">
                            {homepageContent?.aboutUs?.title || 'VÍCE O NÁS'}
                        </div>
                        <div className="container mx-auto px-4 pt-16">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {homepageContent?.aboutUs?.cards?.length > 0 ? (
                                        homepageContent.aboutUs.cards.map((card, index) => (
                                            <div key={index} className="card p-6 text-center bg-gray-700 rounded-xl shadow-lg">
                                                {card.icon && icons[card.icon] ? (
                                                    React.createElement(icons[card.icon], {
                                                        className: "mx-auto h-12 w-12 text-red-500 mb-4"
                                                    })
                                                ) : card.icon ? (
                                                    <div className="mx-auto h-12 w-12 text-red-500 mb-4 flex items-center justify-center text-xl font-bold">?</div>
                                                ) : (
                                                    <div className="mx-auto h-12 w-12 mb-4"></div>)}
                                                <h3 className="text-lg font-semibold mb-2 text-white" style={{ color: card?.titleColor || '#ffffff' }}>{card.title}</h3>
                                                <p className="text-gray-400 text-sm" style={{ color: card?.descriptionColor || '#9CA3AF' }}>{card.description}</p>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No About Us cards available.</p>
                                    )}
                                </div>
                                <div className="about-us-text-section bg-gray-700 p-8 rounded-xl shadow-lg">
                                    <p className="text-gray-300 mb-6 text-right" style={{ color: homepageContent?.aboutUs?.textColor || '#D1D5DB' }}>
                                        {homepageContent?.aboutUs?.text || 'Loading About Us text...'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                    <div className="h-2 bg-white w-full"></div>
                    <section id='vehicles' className="relative py-12 md:py-16 lg:py-20" style={getSectionStyle('vehicles')}>
                        <div className="section-title-bg mt-5 section-title-left bg-white text-gray-900 py-3 px-12 absolute top-0 left-0 font-bold text-2xl rounded-br-2xl transform -translate-y-1/2">
                            VOZIDLA
                        </div>
                        <div className="container mx-auto px-4 pt-16">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                                {vehicles.length === 0 ? (
                                    <p>No vehicles available.</p>
                                ) : (
                                    vehicles.map(vehicle => (
                                        <Card key={vehicle.id} image={vehicle.photoURL} title={vehicle.name} description={vehicle.description} />
                                    ))
                                )}
                            </div>
                        </div>
                    </section>
                    <div className="h-2 bg-white w-full"></div>
                    <section id='members' className="relative py-12 md:py-16 lg:py-20" style={getSectionStyle('members')}>
                        <div className="section-title-bg mt-5 section-title-right bg-white text-gray-900 py-3 px-12 absolute top-0 right-0 font-bold text-2xl rounded-bl-2xl transform -translate-y-1/2">
                            ČLENOVÉ
                        </div>
                        <div className="container mx-auto px-4 pt-16">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                                {members.length === 0 ? (
                                    <p>No members available.</p>
                                ) : (
                                    members.map(member => (
                                        <MemberCard key={member.id} image={member.profilePictureUrl} name={`${member.firstName} ${member.lastName}`} position={member.position} />
                                    ))
                                )}
                            </div>
                        </div>
                    </section>
                    <Footer />
                </>
            )}
        </>
    );
};
export default App;